import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { faqItems, portfolioFaqItems } from '../../mocks/faq';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('todas');
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.05 });

  useEffect(() => {
    document.title = 'Perguntas Frequentes | STIB Palestra Silenciosa São Paulo | FAQ';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Tire todas as suas dúvidas sobre palestra silenciosa, equipamentos de áudio sem fio e serviços STIB. Perguntas frequentes sobre conferência silenciosa, silent disco e eventos corporativos.');
    }

    const lastModified = document.querySelector('meta[name="last-modified"]');
    if (lastModified) {
      lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    }

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/faq#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: { '@id': siteUrl, name: 'Home' },
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Perguntas Frequentes',
          item: { '@id': `${siteUrl}/faq`, name: 'Perguntas Frequentes' },
        },
      ],
    };

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.id = 'faq-breadcrumb-schema';
    breadcrumbScript.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    const faqPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: `${siteUrl}/faq`,
      name: 'Perguntas Frequentes - STIB Palestra Silenciosa',
      mainEntity: [...faqItems, ...portfolioFaqItems].map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    };

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'faq-page-schema';
    faqScript.text = JSON.stringify(faqPageSchema);
    document.head.appendChild(faqScript);

    return () => {
      const existingBreadcrumb = document.getElementById('faq-breadcrumb-schema');
      if (existingBreadcrumb) existingBreadcrumb.remove();
      const existingFaq = document.getElementById('faq-page-schema');
      if (existingFaq) existingFaq.remove();
    };
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const categoryLabels: Record<string, string> = {
    todas: 'Todas as Perguntas',
    Equipamentos: 'Equipamentos',
    Serviços: 'Serviços',
    Logística: 'Logística',
  };

  const categories = ['todas', 'Equipamentos', 'Serviços', 'Logística'];

  const allFAQs = [...faqItems, ...portfolioFaqItems];

  const filteredFAQs = activeCategory === 'todas'
    ? allFAQs
    : portfolioFaqItems.filter((item) => item.category === activeCategory);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-white dark:bg-gray-900">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 to-secondary/10 dark:from-gray-800 dark:to-gray-900 pt-36 pb-20">
          <div className="max-w-6xl mx-auto px-6">
            {/* Breadcrumb visual */}
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-2 text-sm font-body mb-8"
            >
              <Link
                to="/"
                className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-4-line text-xs"></i>
                </div>
                Home
              </Link>
              <div className="w-4 h-4 flex items-center justify-center text-gray-300 dark:text-gray-600">
                <i className="ri-arrow-right-s-line text-sm"></i>
              </div>
              <span
                aria-current="page"
                className="text-primary font-semibold"
              >
                Perguntas Frequentes
              </span>
            </nav>

            <div
              ref={headerRef}
              className={`text-center transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <span className="inline-block font-body text-primary font-semibold text-sm uppercase tracking-widest mb-4">
                Tire suas dúvidas
              </span>
              <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl text-secondary dark:text-white mb-8">
                PERGUNTAS<br />
                <span className="text-primary dark:text-primary">FREQUENTES</span>
              </h1>
              <p className="font-body text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Respostas completas sobre nossa tecnologia de palestra silenciosa, equipamentos profissionais e serviços de áudio sem fio para eventos em todo o Brasil.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-800">
              {/* Category Filter */}
              <div className="mb-8">
                <h3 className="font-heading font-semibold text-2xl text-secondary dark:text-white mb-6">
                  Filtrar por Categoria
                </h3>
                <div className="flex flex-wrap gap-3">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => { setActiveCategory(category); setOpenIndex(null); }}
                      className={`px-6 py-3 rounded-full font-body font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        activeCategory === category
                          ? 'bg-primary text-white transform scale-105'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {categoryLabels[category]}
                    </button>
                  ))}
                </div>
              </div>

              {/* FAQ Count */}
              <div className="p-4 bg-primary/5 dark:bg-primary/10 rounded-xl">
                <p className="font-body text-gray-700 dark:text-gray-300">
                  <span className="font-semibold text-primary">{filteredFAQs.length} perguntas</span> encontradas
                  {activeCategory !== 'todas' && (
                    <span> na categoria <strong>{categoryLabels[activeCategory]}</strong></span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-5xl mx-auto px-6">
            <div ref={faqRef} className="space-y-0">
              {filteredFAQs.map((item, index) => (
                <div
                  key={`${item.id}-${activeCategory}`}
                  className={`border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-all duration-500 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: faqVisible ? `${index * 60}ms` : '0ms' }}
                >
                  {'category' in item && (
                    <div className="px-8 pt-6">
                      <span className="inline-block px-3 py-1 text-sm font-medium text-primary bg-primary/10 dark:bg-primary/20 rounded-full">
                        {item.category}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full py-8 px-8 flex items-center justify-between text-left cursor-pointer group"
                  >
                    <h3 className="font-heading font-semibold text-xl text-secondary dark:text-white pr-8 group-hover:text-primary dark:group-hover:text-primary transition-colors duration-300">
                      {item.question}
                    </h3>
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-primary/10 dark:bg-primary/20 rounded-full group-hover:bg-primary group-hover:text-white transition-all duration-300">
                      <i
                        className={`${openIndex === index ? 'ri-subtract-line' : 'ri-add-line'} text-xl transition-all duration-300`}
                      ></i>
                    </div>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-400 ease-in-out ${
                      openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-8 pb-8">
                      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
                        <p className="font-body text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                          {item.answer}
                        </p>
                        {'relatedPost' in item && item.relatedPost && (
                          <Link
                            to={`/blog/${item.relatedPost.slug}`}
                            className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-primary hover:underline group cursor-pointer"
                          >
                            <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary transition-all duration-200 flex-shrink-0">
                              <i className="ri-article-line text-xs text-primary group-hover:text-white transition-colors duration-200" />
                            </div>
                            Leia o guia completo: {item.relatedPost.title}
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-primary to-secondary py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
              Ainda tem dúvidas?
            </h2>
            <p className="font-body text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Nossa equipe técnica está pronta para esclarecer qualquer dúvida específica sobre o seu evento.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contato"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-heading font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-message-3-line text-xl mr-3"></i>
                Falar com Especialista
              </Link>
              <a
                href="tel:+5511917776227"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-heading font-semibold rounded-full hover:bg-white hover:text-primary transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl mr-3"></i>
                (11) 91777-6227
              </a>
            </div>
          </div>
        </section>

        {/* Internal Blog Links */}
        <section className="py-16 bg-white dark:bg-gray-900">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <span className="font-body text-primary font-semibold text-sm uppercase tracking-widest mb-3 block">
                Aprofunde-se no assunto
              </span>
              <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white">
                Guias Completos sobre Palestra Silenciosa
              </h2>
              <p className="font-body text-gray-500 dark:text-gray-400 mt-3 max-w-xl mx-auto text-sm">
                Artigos detalhados sobre como a tecnologia funciona, casos de uso reais e como contratar no Brasil.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link
                to="/blog/palestra-silenciosa-o-que-e-como-funciona-guia-completo"
                className="group flex gap-5 bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:bg-white dark:hover:bg-gray-700 border-2 border-transparent hover:border-primary/20 dark:border-gray-700 transition-all duration-300 cursor-pointer"
              >
                <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden">
                  <img
                    src="/img/stib/fone-plateia-media.webp"
                    alt="Palestra Silenciosa: O Que É e Como Funciona"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-xs font-semibold text-primary bg-primary/10 dark:bg-primary/20 px-2 py-1 rounded-full mb-2">
                    Guia Completo · 12 min
                  </span>
                  <h3 className="font-heading font-bold text-base text-secondary dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors duration-300 leading-snug mb-2">
                    Palestra Silenciosa: O Que É, Como Funciona e Por Que Seu Evento Precisa Disso
                  </h3>
                  <p className="font-body text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Como funciona a tecnologia RF, benefícios, specs dos equipamentos e como contratar no Brasil.
                  </p>
                </div>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 group-hover:bg-primary transition-all duration-300 self-center">
                  <i className="ri-arrow-right-line text-primary group-hover:text-white text-sm transition-colors duration-300"></i>
                </div>
              </Link>

              <Link
                to="/blog/conferencia-silenciosa-silent-conference-brasil-guia-completo"
                className="group flex gap-5 bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:bg-white dark:hover:bg-gray-700 border-2 border-transparent hover:border-primary/20 dark:border-gray-700 transition-all duration-300 cursor-pointer"
              >
                <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden">
                  <img
                    src="/img/stib/fone-auditorio-alto.webp"
                    alt="Conferência Silenciosa no Brasil: Guia Definitivo"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-xs font-semibold text-primary bg-primary/10 dark:bg-primary/20 px-2 py-1 rounded-full mb-2">
                    Guia Completo · 14 min
                  </span>
                  <h3 className="font-heading font-bold text-base text-secondary dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors duration-300 leading-snug mb-2">
                    Conferência Silenciosa no Brasil: O Guia Definitivo para um Silent Conference de Sucesso
                  </h3>
                  <p className="font-body text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    Comparação com sistemas tradicionais, planejamento passo a passo e checklist completo.
                  </p>
                </div>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 group-hover:bg-primary transition-all duration-300 self-center">
                  <i className="ri-arrow-right-line text-primary group-hover:text-white text-sm transition-colors duration-300"></i>
                </div>
              </Link>

              <Link
                to="/blog/silent-disco-brasil-festa-silenciosa-guia-completo"
                className="group flex gap-5 bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 hover:bg-white dark:hover:bg-gray-700 border-2 border-transparent hover:border-primary/20 dark:border-gray-700 transition-all duration-300 cursor-pointer"
              >
                <div className="flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden">
                  <img
                    src="/img/stib/festa-silent-disco.webp"
                    alt="Silent Disco no Brasil: Guia Completo sobre Festa Silenciosa"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="inline-block text-xs font-semibold text-primary bg-primary/10 dark:bg-primary/20 px-2 py-1 rounded-full mb-2">
                    Guia Completo · 13 min
                  </span>
                  <h3 className="font-heading font-bold text-base text-secondary dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors duration-300 leading-snug mb-2">
                    Silent Disco no Brasil: O Guia Completo sobre Festa Silenciosa
                  </h3>
                  <p className="font-body text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    O que é silent disco, como funciona, formatos, custos e por que domina os eventos no Brasil.
                  </p>
                </div>
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20 group-hover:bg-primary transition-all duration-300 self-center">
                  <i className="ri-arrow-right-line text-primary group-hover:text-white text-sm transition-colors duration-300"></i>
                </div>
              </Link>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-16 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-6xl mx-auto px-6">
            <h3 className="font-heading font-bold text-3xl text-center text-secondary dark:text-white mb-12">
              Explore Nossos Serviços
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Link
                to="/servicos"
                className="group bg-white dark:bg-gray-900 p-8 rounded-2xl hover:border-primary/30 transition-all duration-300 transform hover:scale-105 border border-gray-100 dark:border-gray-800 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 dark:bg-primary/20 rounded-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <i className="ri-headphone-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
                </div>
                <h4 className="font-heading font-semibold text-xl text-secondary dark:text-white mb-4">
                  Nossos Serviços
                </h4>
                <p className="font-body text-gray-600 dark:text-gray-300">
                  Conheça todas as soluções de palestra silenciosa e serviços para eventos corporativos
                </p>
              </Link>

              <Link
                to="/portfolio"
                className="group bg-white dark:bg-gray-900 p-8 rounded-2xl hover:border-primary/30 transition-all duration-300 transform hover:scale-105 border border-gray-100 dark:border-gray-800 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 dark:bg-primary/20 rounded-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <i className="ri-gallery-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
                </div>
                <h4 className="font-heading font-semibold text-xl text-secondary dark:text-white mb-4">
                  Portfólio
                </h4>
                <p className="font-body text-gray-600 dark:text-gray-300">
                  Veja exemplos reais de eventos com nossa tecnologia de áudio sem fio
                </p>
              </Link>

              <Link
                to="/blog"
                className="group bg-white dark:bg-gray-900 p-8 rounded-2xl hover:border-primary/30 transition-all duration-300 transform hover:scale-105 border border-gray-100 dark:border-gray-800 cursor-pointer"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary/10 dark:bg-primary/20 rounded-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <i className="ri-article-line text-2xl text-primary group-hover:text-white transition-colors duration-300"></i>
                </div>
                <h4 className="font-heading font-semibold text-xl text-secondary dark:text-white mb-4">
                  Blog Técnico
                </h4>
                <p className="font-body text-gray-600 dark:text-gray-300">
                  Artigos detalhados sobre tecnologia e tendências para eventos
                </p>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
